/* eslint-disable dot-notation */
import {
  ChangeEvent,
  FC, SyntheticEvent, useCallback, useEffect, useState,
} from 'react';
import {
  Autocomplete,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { some } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';

import { IPaginationModel } from 'interfaces/IPaginationModel';
import useDebounce from 'hooks/useDebounce';
import { ColorTheme } from 'enum/ColorTheme';

import './AppSelect.scss';

export interface ISearchRequest extends IPaginationModel {
  searchValue?: string
}

const DROPDOWN_HEIGHT = 180;

interface IComponentProps {
  name?: string;
  required?: boolean;
  disabled?: boolean;
  fieldStyle?: any;
  formItemName?: string;
  error?: boolean;
  helperText?: string;
  options?: any[] | null;
  isLoading?: boolean;
  getOptions?: (value: ISearchRequest) => void;
  async?: boolean;
  selectedOption?: any;
  setValueProperty?: string | null;
  onChangeValue?: (value: any) => void;
  getOptionValue?: (option: any) => string;
  getOptionLabel?: (option: any) => string;
  totalCount?: number;
  disabledOptions?: any[];
  trigger?: object;
  className?: string;
  placeholder?: string,
  getOptionDisabled?: (value: any) => boolean;
  dataTestId?: string,
  disableClearable?: boolean,
  theme?: ColorTheme,
  isOptionEqualToValue?: (option, value) => boolean;
}

const AppSelectSearch: FC<IComponentProps> = ({
  name,
  required = false,
  disabled = false,
  fieldStyle,
  formItemName = '',
  options,
  isLoading,
  getOptions,
  onChangeValue,
  setValueProperty = '_id',
  async = false,
  selectedOption,
  getOptionValue,
  totalCount,
  getOptionLabel,
  disabledOptions,
  trigger,
  getOptionDisabled,
  className = '',
  placeholder,
  dataTestId = '',
  disableClearable = false,
  theme = ColorTheme.BLUE,
  isOptionEqualToValue,
}) => {
  const ctx = useFormContext();

  const [open, setOpen] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<IPaginationModel>({
    pageSize: 5,
    page: 1,
  });
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [value, setValue] = useState<any>(selectedOption);

  const debounceSearchValue = useDebounce(searchValue);

  useEffect(() => {
    if (!options) {
      setPaginationModel((prev) => ({
        ...prev,
        page: 1,
      }));
    }
  }, [options]);

  useEffect(() => {
    setValue(selectedOption);
    setSearchValue(selectedOption || '');
  }, [selectedOption, trigger]);

  useEffect(() => {
    if (open) {
      getOptions?.({
        searchValue: debounceSearchValue,
        ...paginationModel,
      });
    }
  }, [debounceSearchValue, paginationModel, open]);

  useEffect(() => {
    if (selectedOption) {
      setValue(selectedOption);
      setSearchValue(getOptionLabel ? getOptionLabel(selectedOption) : selectedOption?.name);
      onChangeValue?.(selectedOption);
    }
  }, [selectedOption, getOptionLabel]);

  const onChangeSearchInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const onChangeSelect = useCallback((e: any, value: any) => {
    setValue(value);

    if (async) {
      setSearchValue(getOptionLabel?.(value) || value?.name);
    }
    if (onChangeValue) {
      onChangeValue(value);
    } else {
      ctx?.setValue(
        formItemName,
        setValueProperty ? value?.[setValueProperty] : value,
      );
    }
  }, [ctx, onChangeValue, async]);

  const getSelectForm = (field?: object) => (
    <FormControlLabel
      className={`app-select ${className}`}
      control={(
        <Autocomplete
          open={open}
          loading={!!isLoading}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          {...(field ?? {})}
          value={value || ''}
          onChange={onChangeSelect}
          disabled={disabled}
          sx={{
            ...fieldStyle,
          }}
          options={options || []}
          blurOnSelect
          openOnFocus
          onBlur={() => setSearchValue('')}
          data-testid={dataTestId}
          disableClearable={disableClearable || !value}
          getOptionLabel={(option) => getOptionLabel?.(option) || option?.name || ''}
          renderOption={(props, option) => (
            <MenuItem
              {...props}
              key={getOptionValue?.(option) || option?.['_id']}
              value={getOptionValue?.(option) || option?.['_id']}
            >
              {getOptionLabel?.(option) || option?.name || ''}
            </MenuItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              value={searchValue}
              required={required}
              onChange={onChangeSearchInput}
              slotProps={{
                input: {
                  ...params.InputProps,
                  placeholder,
                },
              }}
            />
          )}
          getOptionDisabled={(option) => {
            if (getOptionDisabled) {
              return getOptionDisabled(option);
            }

            return disabledOptions ? some(disabledOptions, option) : false;
          }}
          isOptionEqualToValue={(option, value) => {
            if (isOptionEqualToValue) {
              return isOptionEqualToValue(option, value);
            }
            return value?.id && option?._id ? option?._id === value?._id : option === value;
          }}
          popupIcon={<FontAwesomeIcon icon={faAngleDown} />}
          slotProps={{
            listbox: {
              sx: { maxHeight: DROPDOWN_HEIGHT },
              className: `app-select-search ${theme}`,
              onScroll: (event: SyntheticEvent) => {
                const listboxNode = event.currentTarget;
                if (
                  Math.trunc(Math.abs(
                    listboxNode.scrollTop + listboxNode.clientHeight - listboxNode.scrollHeight,
                  )) <= 1 && async
                  && totalCount
                  && (paginationModel.page * paginationModel.pageSize < totalCount)
                ) {
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }));
                }
              },
            },
          }}
        />
      )}
      required={required}
      label={name}
      labelPlacement="top"
    />
  );

  return (
    formItemName ? (
      <Controller
        name={formItemName}
        control={ctx.control}
        render={({ field }) => (
          getSelectForm(field)
        )}
      />
    ) : getSelectForm()
  );
};

export default AppSelectSearch;
